import type { ReactNode } from 'react';

// =========================================================================================
// aiOverrideField — 地区モードの「項目ごと継承トグル」ラッパ (Phase 4)
// =========================================================================================
// 継承中 (overridden=false): ラベル横に「✓この地区で上書き」チェック、本体は淡色 (グレー)
//   で実効値を表示しつつ入力 disabled。source ('template' | 'override') もバッジ表示。
// 上書き中 (overridden=true): 通常色で編集可 + 「↩テンプレに戻す」リンク。
// locked=true: マスタ専管項目 (rules)。トグル無し・常にグレー・常に template 表示。
// =========================================================================================

export interface OverrideFieldProps {
  /** 項目名 (アコーディオン内の小見出し) */
  label: string;
  /** resolveEffectiveAiAnalysis 由来の source */
  source: 'template' | 'override';
  /** この地区で上書き中か */
  overridden: boolean;
  /** トグル ON/OFF ハンドラ (locked のとき未指定) */
  onToggle?: (next: boolean) => void;
  /** override を破棄して継承へ戻す (= onToggle(false) と同等の明示リンク) */
  onReset?: () => void;
  /** マスタ専管 (地区で変更不可) */
  locked?: boolean;
  /** 本体 (入力 UI)。継承中は disabled 表現でグレー表示される */
  children: ReactNode;
}

export function OverrideField(props: OverrideFieldProps) {
  const { label, source, overridden, onToggle, onReset, locked, children } = props;

  const editable = !locked && overridden;

  return (
    <div className="border border-slate-200 rounded-lg p-3 space-y-2">
      <div className="flex items-center justify-between gap-2">
        <span className="flex items-center gap-2 text-sm font-medium text-slate-700">
          {label}
          {locked ? (
            <span className="inline-flex items-center gap-1 text-xs font-normal text-slate-400">
              <span aria-hidden="true">🔒</span>マスタ専管
            </span>
          ) : (
            <SourceBadge source={source} overridden={overridden} />
          )}
        </span>
        {!locked && (
          <label className="flex items-center gap-1.5 text-sm text-slate-600 cursor-pointer select-none">
            <input
              type="checkbox"
              checked={overridden}
              onChange={(e) => onToggle?.(e.target.checked)}
            />
            <span>この地区で上書き</span>
          </label>
        )}
      </div>

      <div
        className={
          editable
            ? 'space-y-2'
            : 'space-y-2 opacity-60 text-slate-400 pointer-events-none select-none'
        }
        aria-disabled={!editable}
      >
        {children}
      </div>

      {editable && onReset != null && (
        <button
          type="button"
          onClick={onReset}
          className="text-xs text-blue-600 hover:text-blue-700"
        >
          ↩ テンプレに戻す
        </button>
      )}
    </div>
  );
}

function SourceBadge(props: { source: 'template' | 'override'; overridden: boolean }) {
  const { source, overridden } = props;
  // overridden が真なら override 由来、偽なら継承中 (template)。
  const isOverride = overridden || source === 'override';
  if (isOverride) {
    return <span className="text-xs font-normal text-emerald-600">この地区で上書き中</span>;
  }
  return <span className="text-xs font-normal text-slate-400">テンプレ継承中</span>;
}
